/**
 * "Last verified" inline badge.
 *
 * Surfaces the `verified_at` value from the DB so a human and an AI
 * extractor both see when this number / page content was last cross-
 * checked against the cited source. The badge format is deliberately
 * machine-parseable: `<time datetime="ISO-8601">human</time>` inside a
 * predictable span.
 */

type Props = {
  isoTimestamp: string | null | undefined;
  /** Optional source label rendered next to the timestamp */
  sourceLabel?: string | null;
  /** Locale for date formatting */
  locale?: string;
  /** Localised "Verified" label — caller supplies the i18n string */
  label?: string;
};

export function VerifiedAt({ isoTimestamp, sourceLabel, locale = "en", label = "Verified" }: Props) {
  if (!isoTimestamp) return null;
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return null;
  const human = date.toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
  return (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <span>{label}:</span>
      <time dateTime={isoTimestamp}>{human}</time>
      {sourceLabel ? (
        <>
          <span aria-hidden="true">·</span>
          <span>{sourceLabel}</span>
        </>
      ) : null}
    </span>
  );
}
